'use client';

import { c as _c } from "react/compiler-runtime";
import { deleteCookie } from 'cookies-next/client';
import { useAtom, useSetAtom } from 'jotai';
import { useParams } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { FaAngleRight, FaArrowRightFromBracket, FaBullhorn, FaEnvelope, FaGear, FaHeadset, FaQuestion, FaRegHeart, FaRegMessage, FaSistrix, FaUser } from 'react-icons/fa6';
import { MdOutlineGridView, MdOutlineHelpOutline } from 'react-icons/md';
import { analyticSetUser } from '@/features/analytic/enteties/analytic';
import { type UserListingsData } from '@/features/listing/types/user-listings-types';
import { type MessagesCounters, MessagesType } from '@/features/messages/types/message-counters-types';
import useAppFlyerLink from '@/features/roomster-app/hooks/use-app-flyer-link';
import { helpdeskDataAtom } from '@/features/user/atoms/helpdesk-data-atom';
import { loggedUserAtom } from '@/features/user/atoms/logged-user-atom';
import { deleteUserBearerCookie, getUserBearerCookie } from '@/features/user/cookies/cookie-user-bearer';
import { type LoggedUser } from '@/features/user/types/user-types';
import { Link, useRouter } from '@/i18n/routing';
import { deleteCookieUUID } from '@/layout/cookie-accept-popup/cookies/cookie-uuid';
import styles from '@/layout/menu/menu.module.scss';
import { EXTERNAL_LINKS, ROUTER_LINKS } from '@/shared/constants/links-constants';
import postReportElastic from '@/shared/requests/post-report-elastic';
import { setSessionStorage } from '@/shared/utils/typed-session-storage';
import Badge from '@/ui/badge/badge';
import NotificationsIndicator from '@/ui/notification-indicator/notifications-indicator';
import MenuLink from "@/layout/menu/menu-link";
interface NavigationProps {
  user: LoggedUser | null;
  messagesCounters: MessagesCounters | null;
  userListingsData: UserListingsData | null;
  closeMenu: () => void;
  searchLink: string;
  isFirstListing?: boolean;
  megaphoneCount: number;
}
export default function Navigation(props) {
  const $ = _c(25);
  const {
    closeMenu,
    isFirstListing,
    megaphoneCount,
    messagesCounters,
    searchLink,
    user,
    userListingsData
  } = props;
  const setLoggedUserData = useSetAtom(loggedUserAtom);
  const [helpdeskData, setHelpdeskData] = useAtom(helpdeskDataAtom);
  const router = useRouter();
  const params = useParams();
  const translationsLinks = useTranslations("global.links");
  const translationsActions = useTranslations("global.actions");
  const translationsMenu = useTranslations("layout.menu");
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = getUserBearerCookie();
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const userBearerCookie = t0;
  const placeAppFlyerLink = useAppFlyerLink("mobile", "mobileMenuListYourSpaceButton", ROUTER_LINKS.phoneAuth);
  const helpAppFlyerLink = useAppFlyerLink("mobile", "mobileMenuHelp", ROUTER_LINKS.phoneAuth);
  const inboxMessages = messagesCounters?.[MessagesType.Inbox];
  const isListingsListPage = !!params.locale && !!params.listingType && params.location && params.location.length > 0;
  let t1;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = () => {
      if (userBearerCookie) {
        postReportElastic({
          bearerToken: userBearerCookie,
          report: {
            action_name: "megaphone_page_request",
            details: {
              src: "menu"
            }
          },
          userAgent: globalThis.navigator.userAgent
        });
      }
    };
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const handleMegaphoneClick = t1;
  let t2;
  if ($[2] !== user) {
    t2 = () => {
      if (user && !user.isPaidAccount) {
        setSessionStorage("paywallActionValue", "paywall_from_menu_messages_button");
      }
      if (userBearerCookie) {
        postReportElastic({
          bearerToken: userBearerCookie,
          report: {
            action_name: "mailbox_page_request",
            details: {
              src: "menu"
            }
          },
          userAgent: globalThis.navigator.userAgent
        });
      }
    };
    $[2] = user;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const handleMessagesClick = t2;
  let t3;
  if ($[4] !== router || $[5] !== setHelpdeskData || $[6] !== setLoggedUserData) {
    t3 = () => {
      new Promise(_temp2).then(() => {
        setLoggedUserData(null);
        setHelpdeskData(null);
        router.replace(ROUTER_LINKS.home);
      }).catch(_temp3);
    };
    $[4] = router;
    $[5] = setHelpdeskData;
    $[6] = setLoggedUserData;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const handleLogout = t3;
  let t4;
  if ($[8] !== closeMenu || $[9] !== handleLogout || $[10] !== handleMessagesClick || $[11] !== helpAppFlyerLink || $[12] !== helpdeskData || $[13] !== inboxMessages || $[14] !== isFirstListing || $[15] !== isListingsListPage || $[16] !== megaphoneCount || $[17] !== placeAppFlyerLink || $[18] !== searchLink || $[19] !== translationsActions || $[20] !== translationsLinks || $[21] !== translationsMenu || $[22] !== user || $[23] !== userListingsData) {
    t4 = <ul className={styles.nav}>{user ? <>{!isFirstListing && <><li>{isListingsListPage ? <MenuLink className={styles.nav_link} href={searchLink} closeMenu={closeMenu}><FaSistrix className={styles.nav_icon} />{translationsLinks("search")}<FaAngleRight className={styles.nav_arrowIcon} /></MenuLink> : <Link className={styles.nav_link} href={searchLink}><FaSistrix className={styles.nav_icon} />{translationsLinks("search")}<FaAngleRight className={styles.nav_arrowIcon} /></Link>}</li><li><MenuLink className={styles.nav_link} href={ROUTER_LINKS.accountBookmarks} closeMenu={closeMenu}><FaRegHeart className={styles.nav_icon} />{translationsLinks("bookmarks")}<FaAngleRight className={styles.nav_arrowIcon} /></MenuLink></li><div className={styles.nav_divider} /><li><MenuLink className={styles.nav_link} href={ROUTER_LINKS.accountProfile} closeMenu={closeMenu}><FaUser className={styles.nav_icon} />{translationsLinks("my-profile")}{user.missingFields.length > 0 && <div className={styles.nav_indicatorsArea}><div className={styles.nav_completeBadge}><Badge>{translationsMenu("complete-profile")}</Badge></div></div>}<FaAngleRight className={styles.nav_arrowIcon} /></MenuLink></li><li><MenuLink className={styles.nav_link} href={ROUTER_LINKS.accountListings} closeMenu={closeMenu}><MdOutlineGridView className={styles.nav_icon} />{translationsLinks("my-listings")}{userListingsData && userListingsData.count > 0 && `(${String(userListingsData.count)})`}{userListingsData?.isDataMissing && <div className={styles.nav_indicatorsArea}><div className={styles.nav_completeBadge}><Badge>{translationsMenu("complete-listings")}</Badge></div></div>}<FaAngleRight className={styles.nav_arrowIcon} /></MenuLink></li><div className={styles.nav_divider} /><li><MenuLink className={styles.nav_link} href={user?.isPaidAccount ? ROUTER_LINKS.messagesInbox : ROUTER_LINKS.accountPayments} onClick={handleMessagesClick} closeMenu={closeMenu}><FaRegMessage className={styles.nav_icon} />{translationsLinks("messages")}<div className={styles.nav_indicatorsArea}>{!!inboxMessages && inboxMessages.countMegaphoneNew > 0 && <div className={styles.nav_messageIndicator}><NotificationsIndicator icon={FaBullhorn} color="blue" text={inboxMessages.countMegaphoneNew > 999 ? "999+" : inboxMessages.countMegaphoneNew} /></div>}{!!inboxMessages && inboxMessages.countDirectNew > 0 && <div className={styles.nav_messageIndicator}><NotificationsIndicator icon={FaEnvelope} text={inboxMessages.countDirectNew > 999 ? "999+" : inboxMessages.countDirectNew} /></div>}</div><FaAngleRight className={styles.nav_arrowIcon} /></MenuLink></li><li><MenuLink className={styles.nav_link} href={ROUTER_LINKS.megaphone} onClick={handleMegaphoneClick} closeMenu={closeMenu}><FaBullhorn className={styles.nav_icon} />{translationsLinks("megaphone")}{megaphoneCount > 0 && <div className={styles.nav_indicatorsArea}><div className={styles.nav_messageIndicator}><NotificationsIndicator color="blue" text={megaphoneCount > 20 ? "20+" : megaphoneCount} /></div></div>}<FaAngleRight className={styles.nav_arrowIcon} /></MenuLink></li><div className={styles.nav_divider} /><li><MenuLink className={styles.nav_link} href={ROUTER_LINKS.settings} closeMenu={closeMenu}><FaGear className={styles.nav_icon} />{translationsLinks("settings")}<FaAngleRight className={styles.nav_arrowIcon} /></MenuLink></li><li><a className={styles.nav_link} href={EXTERNAL_LINKS.faq}><FaQuestion className={styles.nav_icon} />{translationsLinks("faq")}<FaAngleRight className={styles.nav_arrowIcon} /></a></li><li className={styles.nav_item}>{!!helpdeskData?.notificationsCountNew && helpdeskData.notificationsCountNew > 0 && <div className={styles.nav_messageIndicatorHelpdesk}><NotificationsIndicator color="orange" text={helpdeskData.notificationsCountNew > 99 ? "99+" : helpdeskData.notificationsCountNew} /></div>}<MenuLink className={styles.nav_link} href={ROUTER_LINKS.support} closeMenu={closeMenu}><FaHeadset className={styles.nav_icon} />{translationsLinks("support")}<FaAngleRight className={styles.nav_arrowIcon} /></MenuLink></li></>}<div className={styles.nav_divider} /><li className={styles.nav_logout}><button className={styles.nav_link} onClick={handleLogout}><FaArrowRightFromBracket className={styles.nav_icon} />{translationsActions("logout")}</button></li></> : <><li><MenuLink className={styles.nav_link} href={placeAppFlyerLink} closeMenu={closeMenu}><MdOutlineGridView className={styles.nav_icon} />{translationsLinks("list-space")}</MenuLink></li><li><MenuLink className={styles.nav_link} href={helpAppFlyerLink} closeMenu={closeMenu}><MdOutlineHelpOutline className={styles.nav_icon} />{translationsLinks("help")}</MenuLink></li></>}</ul>;
    $[8] = closeMenu;
    $[9] = handleLogout;
    $[10] = handleMessagesClick;
    $[11] = helpAppFlyerLink;
    $[12] = helpdeskData;
    $[13] = inboxMessages;
    $[14] = isFirstListing;
    $[15] = isListingsListPage;
    $[16] = megaphoneCount;
    $[17] = placeAppFlyerLink;
    $[18] = searchLink;
    $[19] = translationsActions;
    $[20] = translationsLinks;
    $[21] = translationsMenu;
    $[22] = user;
    $[23] = userListingsData;
    $[24] = t4;
  } else {
    t4 = $[24];
  }
  return t4;
}
function _temp3() {
  return null;
}
function _temp2(resolve) {
  resolve(_temp());
}
function _temp() {
  analyticSetUser(null);
  deleteUserBearerCookie();
  deleteCookieUUID();
  deleteCookie("loggedUserLocalizationSettings");
  return true;
}